import { isBlankLayoutRoute } from '../constants/routes'
import { isInAtlasModuleFrame } from './atlasModuleFrame'

/**
 * The pages visited in this tab, newest last, so the app can offer a way back.
 *
 * Atlas is installed as a standalone PWA, which has no browser back button — this
 * trail is the replacement, so it has to be a faithful visit stack rather than a
 * route hierarchy. Two tabs of one record are two entries: leaving a BBC's
 * receivables tab for its payables tab and pressing back has to return to
 * receivables, the same as anywhere else.
 *
 * sessionStorage rather than redux: the trail has to survive a full reload of a
 * detail page (the store does not) while staying per-tab and resetting with the
 * session, which is exactly what the OAuth flow already relies on it for. It
 * also needs no reducer wiring, so recording a visit is a plain function call.
 *
 * Every read and write is best-effort: storage throws when site data is blocked
 * (Safari private browsing raises on `setItem`), and a missing trail is not
 * worth taking a page down for.
 */

const NAV_HISTORY_KEY = 'atlas.navigationHistory.v1'

/** Deep enough that a run of tab hops cannot push the page you came from off the end. */
const MAX_HISTORY_ENTRIES = 10

export interface IVisitedPage {
  pathname: string
  title: string
  /** Kept so a way back returns to the view the user left, not the page's default tab. */
  search?: string
  /** Same, one level finer: the `#section-<key>` a long scrolling page was left at. */
  hash?: string
}

const isVisitedPage = (value: unknown): value is IVisitedPage => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const { pathname, title, search, hash } = value as Partial<IVisitedPage>
  return (
    typeof pathname === 'string' &&
    pathname !== '' &&
    typeof title === 'string' &&
    (search === undefined || typeof search === 'string') &&
    (hash === undefined || typeof hash === 'string')
  )
}

const readHistory = (): IVisitedPage[] => {
  try {
    const raw = window.sessionStorage.getItem(NAV_HISTORY_KEY)
    if (!raw) {
      return []
    }
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(isVisitedPage) : []
  } catch {
    return []
  }
}

const writeHistory = (history: IVisitedPage[]): void => {
  // A same-origin frame shares the tab's sessionStorage, and its pages are not the tab's visits.
  if (isInAtlasModuleFrame()) {
    return
  }
  try {
    window.sessionStorage.setItem(NAV_HISTORY_KEY, JSON.stringify(history))
  } catch {
    // ignore (private mode / quota / unavailable)
  }
}

/**
 * Records the page currently on screen. Safe to call on every render pass: a
 * repeat of the top entry overwrites it rather than growing the trail, which
 * also lets a title that resolves late (an entity name arriving after its
 * request) replace the placeholder the page first rendered with.
 *
 * `keepExistingTitle` leaves that top entry's title alone, so the shell's generic
 * route label cannot outrun the specific title a page set for itself, whichever
 * effect happens to run last.
 *
 * `replace` mirrors the router: a replaced navigation takes the previous entry's place
 * rather than stacking on it. Redirect routes use it (a BBC's collateral-eligibility path
 * forwards to the receivables or inventory form), and without it the trail keeps a step
 * that only ever forwards the user back to where they just were.
 */
export const pushVisitedPage = (
  { pathname, title, search, hash }: IVisitedPage,
  {
    keepExistingTitle = false,
    replace = false,
  }: { keepExistingTitle?: boolean; replace?: boolean } = {},
): void => {
  // Login and the other anonymous routes are never somewhere to send a user back to.
  if (!pathname || !title.trim() || isBlankLayoutRoute(pathname)) {
    return
  }

  const history = readHistory()
  const previous = history[history.length - 1]
  // An empty search or hash is left off the entry entirely, so a link points at a bare path.
  const entry = { pathname, title, ...(search ? { search } : {}), ...(hash ? { hash } : {}) }

  if (previous?.pathname === pathname) {
    const resolvedTitle = keepExistingTitle ? previous.title : entry.title
    writeHistory([...history.slice(0, -1), { ...entry, title: resolvedTitle }])
    return
  }

  writeHistory([...(replace ? history.slice(0, -1) : history), entry].slice(-MAX_HISTORY_ENTRIES))
}

/**
 * Unwinds the stored trail to just before `page`, so going back to it leaves a stack that
 * ends where the user is heading rather than growing past it.
 *
 * Without this a back navigation records like any other visit: from C the trail offers B,
 * and once on B it offers C straight back, so repeated clicks flip between the last two
 * pages and never reach anything older.
 */
export const popVisitedPagesTo = ({ pathname, search, hash }: IVisitedPage): void => {
  const history = readHistory()
  const matches = (entry: IVisitedPage) =>
    entry.pathname === pathname &&
    (entry.search ?? '') === (search ?? '') &&
    (entry.hash ?? '') === (hash ?? '')

  // The newest match: the same page may sit in the trail more than once.
  for (let index = history.length - 1; index >= 0; index -= 1) {
    if (matches(history[index])) {
      writeHistory(history.slice(0, index))
      return
    }
  }
}

/**
 * The pages behind the one on screen, nearest first — `trail[0]` is where "back"
 * goes. Empty on a fresh tab or a deep link, which is what tells the UI it has
 * nothing to offer.
 *
 * Collapsed by title, newest kept. A page that redirects to a variant of itself (a BBC's
 * collateral eligibility route forwards to its receivables or inventory form) leaves two
 * pathnames under one title, and a menu listing the same words twice offers no choice
 * between them. Dropping the older one cannot move `trail[0]`, so back is unaffected.
 */
export const getVisitedTrail = (currentPathname: string): IVisitedPage[] => {
  const seenTitles = new Set<string>()

  return readHistory()
    .filter(({ pathname }) => pathname !== currentPathname)
    .reverse()
    .filter(({ title }) => {
      if (seenTitles.has(title)) {
        return false
      }
      seenTitles.add(title)

      return true
    })
}
